import {
  addSlideTagsViaIntents,
  getSlideTagsViaIntents,
} from "../../services/factset/intentBridgeService";
import type { ModificationCitation } from "../../types";

import { CITATION_REFS_TAG_KEY } from "./citationPool";
import type { SlideCitationRefs } from "../stripAndExtract";

type Logger = (msg: string) => void;

function parseRefs(raw: string | undefined): Record<string, string[]> {
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return {};
    const out: Record<string, string[]> = {};
    for (const [key, value] of Object.entries(parsed as Record<string, unknown>)) {
      if (Array.isArray(value)) {
        out[key] = value.filter((v): v is string => typeof v === "string");
      }
    }
    return out;
  } catch {
    return {};
  }
}

function mergeRefs(
  existing: Record<string, string[]>,
  incoming: Record<string, string[]>
): Record<string, string[]> {
  const merged: Record<string, string[]> = { ...existing };
  for (const [shapeId, ids] of Object.entries(incoming)) {
    const current = merged[shapeId] ? merged[shapeId].slice() : [];
    for (const id of ids) {
      if (current.indexOf(id) === -1) current.push(id);
    }
    merged[shapeId] = current;
  }
  return merged;
}

export function reduceModificationCitationsToValueMap(
  citations: ModificationCitation[] | undefined | null
): Record<string, string> {
  const out: Record<string, string> = {};
  if (!Array.isArray(citations) || citations.length === 0) return out;
  for (const entry of citations) {
    if (!entry || typeof entry !== "object") continue;
    const rawId = (entry as { id?: unknown }).id;
    const id = typeof rawId === "string" ? rawId.trim() : "";
    if (!id || id in out) continue;
    const rawValue = (entry as { value?: unknown }).value;
    const value = typeof rawValue === "string" ? rawValue.trim() : "";
    if (!value) continue;
    out[id] = value;
  }
  return out;
}

export async function persistCitationRefs(
  buckets: SlideCitationRefs[],
  log?: Logger
): Promise<void> {
  if (!Array.isArray(buckets) || buckets.length === 0) return;

  for (const bucket of buckets) {
    if (!bucket.slideId || Object.keys(bucket.refsByShape).length === 0) {
      if (!bucket.slideId) {
        log?.(
          `[citationPool] skipping refs for slide index ${bucket.slideIndex} (no slideId resolved at strip time).`
        );
      }
      continue;
    }
    try {
      const existingTags = await getSlideTagsViaIntents({ slideId: bucket.slideId, log });
      const existing = parseRefs(existingTags?.[CITATION_REFS_TAG_KEY]);
      const merged = mergeRefs(existing, bucket.refsByShape);
      const serialized = JSON.stringify(merged);
      await addSlideTagsViaIntents({
        slideId: bucket.slideId,
        tags: { [CITATION_REFS_TAG_KEY]: serialized },
        log,
      });
      log?.(
        `[citationPool] wrote refs for slide ${bucket.slideIndex + 1}: ${serialized.length} bytes, ${Object.keys(merged).length} shape(s).`
      );
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error);
      log?.(`[citationPool] persistCitationRefs failed for slide ${bucket.slideIndex + 1}: ${msg}`);
    }
  }
}
